import {I18nFunction} from 'i18n';
import {Locale} from 'contexts/LocaleContext';
import {Env} from 'contexts/EnvContext';
import {NavigationItemType} from 'units/navigation/models';
import {getGroupItems, getSocialItems} from './header/utils';
import {getConsoleLink, getServiceLink} from 'utils';
import {BadgePlatform, Platform} from '@yandex-data-ui/common';
import metrika from 'counters/metrika';
import {ModalFormType} from 'units/constructor-local/components/ModalForm/utils';

export const SERVICES_PREFIX = 'services';
export const SOLUTIONS_PREFIX = 'solutions';
export const MARKETPLACE_PREFIX = 'marketplace';
const COMMON_PREFIX = 'common';
const FEATURE_PREFIX = 'features';
const FEATURE_ITEMS = [
    'prices',
    'for-business',
    'for-education-and-science',
    'cases',
    'security',
    'insurance-offer',
    'partners/catalogue',
    'docs/support/overview',
    'solutions/gateway-to-russia',
];
const COMMUNITY_PREFIX = 'community';
const COMMUNITY_ITEMS = ['features', 'content-program', 'events', 'blog'];
const CONTACTS_PREFIX = 'contacts';
const CONTACT_EMAIL = 'cloud@support.yandex.ru';

export enum DropdownTypes {
    Default,
    Extended,
}

const getLegalDomain = (locale: Locale) => {
    switch (locale.region) {
        case 'ru':
            return 'ru';
        case 'kz':
            return 'kz';
        case 'int':
        default:
            return 'com';
    }
};

export default function getConfigItems(locale: Locale, i18n: I18nFunction, env: Env) {
    const {lang, region, tld} = locale;
    const i18nK = i18n.bind(null, 'navigation');

    return {
        common: {
            prices: {
                title: i18nK(`${COMMON_PREFIX}_prices`),
                url: '/prices',
            },
            docs: {
                title: i18nK(`${COMMON_PREFIX}_docs`),
                url: '/docs',
            },
            support: {
                title: i18nK(`${COMMON_PREFIX}_support`),
                url: `${getConsoleLink(tld, env)}/support`,
            },
            statusboard: {
                title: i18nK(`${COMMON_PREFIX}_statusboard`),
                url: `${getServiceLink('status', tld, env)}/dashboard`,
            },
            telegram: {
                title: i18nK(`${COMMON_PREFIX}_telegram`),
                url: '/blog/posts/2021/02/yc-telegram',
                blank: true,
            },
            marketplace: {
                title: i18nK(`${COMMON_PREFIX}_marketplace`),
                url: `/${MARKETPLACE_PREFIX}`,
            },
            products: {
                title: i18nK(`${COMMON_PREFIX}_products`),
                url: `/${MARKETPLACE_PREFIX}`,
                type: NavigationItemType.Link,
            },
            services: {
                title: i18nK(`${COMMON_PREFIX}_services`),
                url: '/services#_all',
            },
            servicesAndTools: {
                title: i18nK(`${COMMON_PREFIX}_services_and_tools`),
                url: '/services#_all',
            },
            events: {title: i18nK(`${COMMON_PREFIX}_events`), url: '/events'},
            sales: {
                title: i18nK(`${COMMON_PREFIX}_sales`),
                url: `#${ModalFormType.ContactSales}`,
            },
            salesAction: {
                title: i18nK(`${COMMON_PREFIX}_sales_action`),
                url: `#${ModalFormType.ContactSales}`,
                type: NavigationItemType.Link,
            },
            confidential: {
                title: i18nK(`${COMMON_PREFIX}_confidential_title`),
                url: `https://yandex.${getLegalDomain(locale)}/legal/confidential/?lang=${lang}`,
                blank: true,
            },
            terms: {
                title: i18nK(`${COMMON_PREFIX}_terms_title`),
                url: `https://yandex.ru/legal/cloud_termsofuse/?lang=${lang}`,
                blank: true,
            },
            job: {
                title: i18nK(`${COMMON_PREFIX}_job_title`),
                url: `https://yandex.${tld}/jobs/services/cloud`,
                blank: true,
            },
            brandbook: {
                title: i18nK(`${COMMON_PREFIX}_brandbook_title`),
                url: '/brandbook',
                blank: true,
            },
            about: {
                title: i18nK(`${COMMON_PREFIX}_about_title`),
                url: '/about',
            },
            contact: {
                title: i18nK(`${CONTACTS_PREFIX}_section_title`),
                url: `#${ModalFormType.ContactSales}`,
            },
            tender: {
                title: i18nK(`${CONTACTS_PREFIX}_tender_invitation`),
                url: `#tender-form`,
            },
            mobileApp: {
                title: i18nK(`${COMMON_PREFIX}_mobileApp`),
                url: '/mobile-app',
                goal: '153_Mobile_App_Doc_Link',
            },
            solutions: {
                title: i18nK(`${COMMON_PREFIX}_solutions`),
                url: '/solutions',
            },
            contacts: {
                title: i18nK(`${COMMON_PREFIX}-contacts`),
            },
            contactEmail: {
                title: CONTACT_EMAIL,
                url: `mailto:${CONTACT_EMAIL}`,
            },
            yandexCloud: {
                title: i18nK(`${COMMON_PREFIX}_yandex_cloud`),
            },
        },

        services: {
            title: i18nK(`${SERVICES_PREFIX}_section_title`),
        },

        features: {
            title: i18nK(`${FEATURE_PREFIX}_section_title`),
            items: getGroupItems(i18n, FEATURE_PREFIX, FEATURE_ITEMS),
            boost: {
                title: i18nK(`${FEATURE_PREFIX}_cloud-boost_title`),
                description: i18nK(`${FEATURE_PREFIX}_cloud-boost_description`),
                url: region === 'kz' ? '/cloud-boost-kz' : '/cloud-boost',
            },
            partnerPropgram: {
                title: i18nK(`${FEATURE_PREFIX}_partners_title`),
                description: i18nK(`${FEATURE_PREFIX}_partners_description`),
                url: '/partners',
            },
        },

        community: {
            title: i18nK(`${COMMUNITY_PREFIX}_section_title`),
            items: getGroupItems(i18n, COMMUNITY_PREFIX, COMMUNITY_ITEMS),
        },

        solutions: {
            title: i18nK(`${SOLUTIONS_PREFIX}_section_title`),
        },

        contacts: {
            title: i18nK(`${CONTACTS_PREFIX}_section_title`),
        },

        social: getSocialItems({
            telegram: {
                url: 'https://t.me/yandexcloudnews',
                title: i18nK('label_telegram'),
            },
            facebook: {
                url: 'https://www.facebook.com/yandexcloud/',
                title: i18nK('label_facebook'),
            },
            vk: {
                url: 'https://vk.com/yandexcloudofficial',
                title: i18nK('label_vk'),
            },
            youtube: {
                url: 'https://www.youtube.com/channel/UCjWOzgG0oTFHy4N4BeDmBhg',
                title: i18nK('label_youtube'),
            },
            github: {
                url: 'https://github.com/yandex-cloud',
                title: i18nK('label_github'),
            },
            rss: {
                url: '/feed.rss',
                title: i18nK('label_rss'),
            },
        }),

        mobileStores: {
            title: i18nK('label_mobile-app'),
            items: [
                {
                    platform: Platform.IOS as BadgePlatform,
                    url: 'https://redirect.appmetrica.yandex.com/serve/963912747546876214',
                    goal: metrika.Goal.MOBILE_STORE_IOS,
                },
                {
                    platform: Platform.ANDROID as BadgePlatform,
                    url: 'https://redirect.appmetrica.yandex.com/serve/315394402329621431',
                    goal: metrika.Goal.MOBILE_STORE_ANDROID,
                },
            ],
        },
    };
}
